import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { newsItems, categoryColors } from "@/mocks/news";

const categories = ["すべて", "お知らせ", "採用情報"];

export default function NewsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeCategory, setActiveCategory] = useState("すべて");

  const filtered = activeCategory === "すべて"
    ? newsItems
    : newsItems.filter((n) => n.category === activeCategory);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: filterRef, isVisible: filterVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: cardsRef, visibleItems: cardItems } = useStaggerAnimation(filtered.length, { threshold: 0.05 });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/fe89334a85f9b2668235eda1bf589204.jpeg"
          alt="株式会社FD お知らせ 訪問介護・障害福祉サービス"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div
          className="absolute inset-0 flex items-center justify-center"
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="text-center">
            <p
              className="text-white/70 text-xs font-['Poppins'] tracking-widest mb-3"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              NEWS
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold text-white tracking-wide"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(25px)",
                transition: "opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s",
              }}
            >
              お知らせ
            </h1>
            <div
              className="flex items-center justify-center gap-2 mt-4 text-xs text-white/60"
              style={{
                opacity: heroVisible ? 1 : 0,
                transition: "opacity 0.8s ease 0.3s",
              }}
            >
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">TOP</Link>
              <span>›</span>
              <span className="text-white/90">お知らせ</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#f7fcfd]">
        <div className="max-w-[1240px] mx-auto px-6">

          {/* Category Filter */}
          <div
            className="flex flex-wrap gap-3 mb-10 justify-center"
            ref={filterRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: filterVisible ? 1 : 0,
              transform: filterVisible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs px-5 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-[#3ab5c6] border-[#3ab5c6] text-white"
                    : "border-gray-300 text-gray-500 hover:border-[#3ab5c6] hover:text-[#3ab5c6]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Cards */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            ref={cardsRef as React.RefObject<HTMLDivElement>}
          >
            {filtered.map((item, i) => (
              <a
                key={i}
                href="#"
                className="bg-white rounded-2xl overflow-hidden cursor-pointer group"
                style={{
                  opacity: cardItems[i] ? 1 : 0,
                  transform: cardItems[i] ? "translateY(0)" : "translateY(35px)",
                  transition: "opacity 0.7s ease, transform 0.7s ease",
                }}
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`text-xs border px-2 py-0.5 rounded-full whitespace-nowrap ${categoryColors[item.category] ?? "text-gray-500 border-gray-300"}`}>
                      {item.category}
                    </span>
                    <span className="text-xs text-gray-400 font-['Poppins']">{item.date}</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-800 leading-snug mb-2 line-clamp-2 group-hover:text-[#3ab5c6] transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{item.excerpt}</p>
                </div>
                <div className="h-0.5 w-0 bg-[#3ab5c6] group-hover:w-full transition-all duration-500 ease-out"></div>
              </a>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400 text-sm">
              該当するお知らせはありません。
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Schema.org Article List */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": newsItems.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "NewsArticle",
              "headline": item.title,
              "description": item.excerpt,
              "datePublished": item.date.replace(/\./g, "-"),
              "author": {
                "@type": "Organization",
                "name": "株式会社FD"
              },
              "publisher": {
                "@type": "Organization",
                "name": "株式会社FD",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://future-diver.jp/logo.png"
                }
              },
              "image": item.img,
              "articleSection": item.category
            }
          }))
        })}
      </script>
    </div>
  );
}
